import React, { useState, useMemo } from "react";
import "./AdminDataTable.css";
import { Eye, X } from "lucide-react";

function AdminDataTable({
    columns = [],
    data = [],
    loading = false,
    pageSize = 10,
    renderRow,
}) {
    const [currentPage, setCurrentPage] = useState(1);
    const [internalPageSize, setInternalPageSize] = useState(pageSize);
    const [viewData, setViewData] = useState(null);

    const totalPages = Math.ceil(data.length / internalPageSize);

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * internalPageSize;
        return data.slice(start, start + internalPageSize);
    }, [data, currentPage, internalPageSize]);

    const handlePrev = () => {
        if (currentPage > 1) setCurrentPage((prev) => prev - 1);
    };

    const handleNext = () => {
        if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
    };

    const handlePageSizeChange = (e) => {
        setInternalPageSize(Number(e.target.value));
        setCurrentPage(1);
    };

    const enhancedColumns = useMemo(() => {
        return columns.map((col) => {
            if (col.key === "actions") {
                return {
                    ...col,
                    render: (val, row) => (
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <div
                                className="action-btn"
                                style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setViewData(row);
                                }}
                                title="View Details"
                            >
                                <Eye size={18} color="#2563eb" />
                            </div>
                            {col.render ? col.render(val, row) : val}
                        </div>
                    ),
                };
            }
            return col;
        });
    }, [columns]);

    return (
        <div className="admin-data-table-wrapper">
            <div className="data-table-controls">
                <div className="rows-per-page">
                    <label>Rows per page: </label>
                    <select value={internalPageSize} onChange={handlePageSizeChange}>
                        {[10, 20, 30, 40, 50].map((size) => (
                            <option key={size} value={size}>
                                {size}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <div className="data-table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            {enhancedColumns.map((col) => (
                                <th key={col.key}>{col.label}</th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={enhancedColumns.length} className="data-table-empty">
                                    <div className="table-loader-container">
                                        <div className="table-loader"></div>
                                        <span>Loading data...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : paginatedData.length === 0 ? (
                            <tr>
                                <td colSpan={enhancedColumns.length} className="data-table-empty">
                                    No Data Found
                                </td>
                            </tr>
                        ) : renderRow ? (
                            paginatedData.map((row, index) => renderRow(row, index))
                        ) : (
                            paginatedData.map((row, index) => (
                                <tr key={index}>
                                    {enhancedColumns.map((col) => {
                                        const isLongText = [
                                            "description",
                                            "remarks",
                                            "notes",
                                            "comments",
                                            "taskname",
                                            "modulename",
                                            "projectname",
                                            "clientname",
                                            "employeename",
                                            "contactperson"
                                        ].includes(col.key);
                                        return (
                                            <td
                                                key={col.key}
                                                className={isLongText ? "description-column" : ""}
                                            >
                                                <div className={isLongText ? "line-clamp-wrapper" : ""}>
                                                    {col.render
                                                        ? col.render(row[col.key], row)
                                                        : row[col.key] || "-"}
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            {totalPages !== 0 && (
                <div className="data-table-pagination">
                    <span className="data-table-pagination-info">
                        Page {currentPage} of {totalPages}
                    </span>

                    <div className="pagination-buttons">
                        <button
                            className="data-table-btn"
                            onClick={handlePrev}
                            disabled={currentPage === 1}
                        >
                            Prev
                        </button>

                        {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                            // Show a few pages
                            const pageNum = index + 1;
                            return (
                                <button
                                    key={pageNum}
                                    className="data-table-btn"
                                    onClick={() => setCurrentPage(pageNum)}
                                    style={{
                                        backgroundColor:
                                            currentPage === pageNum ? "#06433e" : "#ffffff",
                                        color: currentPage === pageNum ? "#fff" : "",
                                    }}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}

                        <button
                            className="data-table-btn"
                            onClick={handleNext}
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {/* Premium View Detail Modal matching AssignedProjects style */}
            {viewData && (
                <div className="premium-dialog-overlay" onClick={() => setViewData(null)}>
                    <div className="premium-view-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-premium">
                            <div className="header-title-group">
                                <div className="header-icon-badge">
                                    <Eye size={20} />
                                </div>
                                <h5>Report Information Details</h5>
                            </div>
                            <button className="modal-close-circle" onClick={() => setViewData(null)}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="modal-body-scrollable">
                            <div className="detail-section-card">
                                {enhancedColumns
                                    .filter(col =>
                                        col.key !== "actions" &&
                                        col.key !== "sno" &&
                                        col.key !== "statusToggle" &&
                                        col.key !== "details"
                                    )
                                    .map((col) => (
                                        <div className="detail-row-item" key={col.key}>
                                            <div className="detail-label-icon">
                                                <span>{col.label}</span>
                                            </div>
                                            <div className="detail-value-text">
                                                {col.render
                                                    ? col.render(viewData[col.key], viewData)
                                                    : viewData[col.key] || "-"}
                                            </div>
                                        </div>
                                    ))
                                }
                            </div>
                        </div>

                        <div className="modal-footer-wrapper">
                            <button className="modal-action-btn" onClick={() => setViewData(null)}>
                                Close details
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminDataTable;
